// Parses a link pasted into the 'Add Manually' tab into something POST /subscriptions accepts:
//   - A Meta ad library page link -> { advertiserId } (sent as `advertiser_ids`)
//   - Any other website           -> { domain }       (sent as `domains`)
// Anything else -> { error }, with a short message for the input. Empty input -> null.

// These mirror the Spyder API's validation (foreplay-spyder-server: src/core/subscriptions/namespace.js)
const ADVERTISER_ID_PATTERN = /^\d{5,25}$/
const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/
const MAX_DOMAIN_LENGTH = 253

const FACEBOOK_HOSTS = ['facebook.com', 'fb.com', 'fb.me']
// The API can't resolve a brand from a social profile yet, and their domains would subscribe to the platform itself
const SOCIAL_HOSTS = [
  ...FACEBOOK_HOSTS,
  'instagram.com',
  'threads.net',
  'tiktok.com',
  'linkedin.com',
  'youtube.com',
  'youtu.be',
  'x.com',
  'twitter.com',
  'pinterest.com',
  'snapchat.com',
  'reddit.com'
]

const LINK_ERRORS = {
  invalid: 'Invalid link',
  social: 'Social profiles aren\'t supported yet',
  adLibrary: 'Use a brand\'s Ad Library page link'
}

const matchesHost = (hostname, hosts) => hosts.some(host => hostname === host || hostname.endsWith(`.${host}`))

const parseUrl = (input) => {
  try {
    // Bare links like nike.com have no scheme for URL to parse
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(input) ? input : `https://${input}`)
  } catch (error) {
    return null
  }
}

const parseSubscriptionLink = (input = '') => {
  const link = input.trim()
  if (!link) return null

  const url = parseUrl(link)
  if (!url || !['http:', 'https:'].includes(url.protocol)) return { error: LINK_ERRORS.invalid }

  // URL lowercases the hostname and drops the port; the API also rejects trailing dots and 'www.'
  const hostname = url.hostname.replace(/\.+$/, '').replace(/^www\./, '')

  if (matchesHost(hostname, FACEBOOK_HOSTS) && url.pathname.startsWith('/ads/library')) {
    // Extract the pageId from the ad library URL (if it exists)
    const pageId = url.searchParams.get('view_all_page_id')?.trim()
    // Keyword searches and single ad links have no page id
    return pageId && ADVERTISER_ID_PATTERN.test(pageId)
      ? { advertiserId: pageId }
      : { error: LINK_ERRORS.adLibrary }
  }

  if (matchesHost(hostname, SOCIAL_HOSTS)) return { error: LINK_ERRORS.social }

  if (hostname.length > MAX_DOMAIN_LENGTH || !DOMAIN_PATTERN.test(hostname)) return { error: LINK_ERRORS.invalid }

  return { domain: hostname }
}

export {
  parseSubscriptionLink
}
